import { centsToEuroInput, euroToCents } from '@/utils/euroAmount'
import { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { validateBox, hasErrors } from '@/utils/validation'
import {
  ArrowLeftIcon,
  TrashIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline'
import { Box as BoxIcon, Package, Wine } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { tipologieBoxApi, configurazioniApi } from '@/api/box'
import { getProdotti } from '@/api/products'
import { fetchAllPaginatedResults } from '@/utils/pagination'

/**
 * Form per creazione/modifica tipologia box.
 * Include selezione configurazione e composizione prodotti.
 */
export default function BoxForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEditing = !!id

  // Form state
  const [formData, setFormData] = useState({
    codice: '',
    nome: '',
    descrizione: '',
    configurazione: '',
    prezzo_vendita_euro: '',
    attivo: true,
    disponibile_ecommerce: false,
    riutilizzabile_come_componente: false,
  })
  const [composizione, setComposizione] = useState([])
  const [immagine, setImmagine] = useState(null)
  const [immaginePreview, setImmaginePreview] = useState(null)
  const [errors, setErrors] = useState({})
  const [tipoVoceComposizione, setTipoVoceComposizione] = useState('prodotto')
  const [searchComposizione, setSearchComposizione] = useState('')
  const [debouncedSearchComposizione, setDebouncedSearchComposizione] = useState('')
  const [showComposizioneDropdown, setShowComposizioneDropdown] = useState(false)
  const dropdownRef = useRef(null)
  const [boxTreeMap, setBoxTreeMap] = useState({})

  const extractApiErrorMessage = (errorData) => {
    if (!errorData) return null
    if (typeof errorData === 'string') return errorData
    if (Array.isArray(errorData)) return errorData.join(' ')
    if (typeof errorData === 'object') {
      if (typeof errorData.error === 'string') return errorData.error
      if (typeof errorData.detail === 'string') return errorData.detail
      const prioritizedFields = ['composizione_input', 'prezzo_vendita_cents', 'configurazione', 'codice', 'nome']
      for (const field of prioritizedFields) {
        const value = errorData[field]
        if (!value) continue
        if (typeof value === 'string') return value
        if (Array.isArray(value)) return value.join(' ')
      }
      const firstKey = Object.keys(errorData)[0]
      const firstValue = errorData[firstKey]
      if (typeof firstValue === 'string') return firstValue
      if (Array.isArray(firstValue)) return firstValue.join(' ')
    }
    return null
  }

  const flattenErrorMessages = (value) => {
    const messages = []
    const visit = (current) => {
      if (!current) return
      if (typeof current === 'string') {
        messages.push(current)
        return
      }
      if (Array.isArray(current)) {
        current.forEach(visit)
        return
      }
      if (typeof current === 'object') {
        Object.values(current).forEach(visit)
      }
    }
    visit(value)
    return messages
  }

  const getFieldErrorMessages = (field) => flattenErrorMessages(errors?.[field])

  // Chiudi dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowComposizioneDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Query dettaglio box (se editing)
  const { data: boxData, isLoading: isLoadingBox } = useQuery({
    queryKey: ['tipologie-box', id],
    queryFn: () => tipologieBoxApi.getById(id),
    enabled: isEditing,
  })

  // Query configurazioni
  const { data: configurazioniData } = useQuery({
    queryKey: ['configurazioni-scatola', { attivo: true, all: true }],
    queryFn: () => fetchAllPaginatedResults(
      (params) => configurazioniApi.getAll(params),
      { attivo: true }
    ),
  })

  const configurazioneIdSelezionata = Number.parseInt(formData.configurazione, 10) || null

  // Query dettaglio configurazione selezionata (per breakdown componenti/costi)
  const { data: configurazioneDettaglioData } = useQuery({
    queryKey: ['configurazioni-scatola', configurazioneIdSelezionata],
    queryFn: () => configurazioniApi.getById(configurazioneIdSelezionata),
    enabled: !!configurazioneIdSelezionata,
  })

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchComposizione(searchComposizione.trim())
    }, 300)

    return () => clearTimeout(timer)
  }, [searchComposizione])

  // Ricerca server-side: il catalogo è paginato e il risultato può trovarsi
  // oltre la prima pagina restituita dall'API.
  const { data: prodottiData, isFetching: isFetchingProdotti } = useQuery({
    queryKey: ['prodotti', 'composizione-box', { search: debouncedSearchComposizione }],
    queryFn: () => getProdotti({
      stato: 'attivo',
      search: debouncedSearchComposizione || undefined,
    }),
    enabled: tipoVoceComposizione === 'prodotto',
  })

  // Query box riutilizzabili come componente (box figlie)
  const { data: boxFiglieData, isFetching: isFetchingBoxFiglie } = useQuery({
    queryKey: ['tipologie-box', 'composizione-box', { search: debouncedSearchComposizione }],
    queryFn: () => tipologieBoxApi.getAll({
      attivo: true,
      riutilizzabile_come_componente: true,
      search: debouncedSearchComposizione || undefined,
    }),
    enabled: tipoVoceComposizione === 'box',
  })

  const configurazioni = Array.isArray(configurazioniData) ? configurazioniData : []

  const prodotti = Array.isArray(prodottiData?.data)
    ? prodottiData.data
    : (prodottiData?.data?.results || [])

  const boxFiglie = Array.isArray(boxFiglieData?.data)
    ? boxFiglieData.data
    : (boxFiglieData?.data?.results || [])

  const boxFiglieSelezionateIds = useMemo(
    () => [...new Set(composizione.filter((c) => c.tipo === 'box' && c.box_figlia).map((c) => c.box_figlia))],
    [composizione]
  )

  useEffect(() => {
    let isMounted = true

    const loadBoxTree = async () => {
      if (!boxFiglieSelezionateIds.length) {
        if (isMounted) setBoxTreeMap({})
        return
      }

      const map = {}
      const queue = [...boxFiglieSelezionateIds]
      const visited = new Set()
      let depth = 0
      const maxDepth = 6

      while (queue.length && depth < maxDepth) {
        const batch = queue.filter((boxId) => !visited.has(boxId))
        queue.length = 0
        if (!batch.length) break

        const responses = await Promise.all(
          batch.map(async (boxId) => {
            try {
              const response = await tipologieBoxApi.getById(boxId)
              return { boxId, data: response?.data || null }
            } catch {
              return { boxId, data: null }
            }
          })
        )

        responses.forEach(({ boxId, data }) => {
          visited.add(boxId)
          if (!data) return
          map[boxId] = data
          ;(data.composizione || [])
            .filter((item) => item?.tipo_voce === 'box' && item?.box_figlia)
            .forEach((item) => {
              if (!visited.has(item.box_figlia)) queue.push(item.box_figlia)
            })
        })

        depth += 1
      }

      if (isMounted) setBoxTreeMap(map)
    }

    loadBoxTree()
    return () => {
      isMounted = false
    }
  }, [boxFiglieSelezionateIds])

  // Popola form se editing
  useEffect(() => {
    if (boxData?.data) {
      const box = boxData.data
      setFormData({
        codice: box.codice || '',
        nome: box.nome || '',
        descrizione: box.descrizione || '',
        configurazione: box.configurazione?.toString() || '',
        prezzo_vendita_euro: centsToEuroInput(box.prezzo_vendita_cents),
        attivo: box.attivo ?? true,
        disponibile_ecommerce: box.disponibile_ecommerce ?? false,
        riutilizzabile_come_componente: box.riutilizzabile_come_componente ?? false,
      })
      if (box.composizione) {
        setComposizione(box.composizione.map(c => ({
          tipo: c.tipo_voce || (c.box_figlia ? 'box' : 'prodotto'),
          prodotto: c.prodotto || null,
          box_figlia: c.box_figlia || null,
          nome: c.prodotto_nome || c.box_figlia_nome || '',
          codice: c.prodotto_codice || c.box_figlia_codice || '',
          costo_unitario_cents: c.costo_unitario_cents || 0,
          quantita: c.quantita,
          note: c.note || '',
        })))
      }
      if (box.immagine_url) {
        setImmaginePreview(box.immagine_url)
      }
    }
  }, [boxData])

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => tipologieBoxApi.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['tipologie-box'] })
      toast.success('Box creata con successo')
      // Gestisci diverse strutture di risposta
      const boxId = response?.data?.id || response?.id
      if (boxId) {
        navigate(`/box/tipologie/${boxId}/modifica`)
      } else {
        navigate('/box/tipologie')
      }
    },
    onError: (error) => {
      const errorData = error.response?.data
      if (errorData) {
        setErrors({ ...errorData, prezzo_vendita_euro: errorData.prezzo_vendita_cents })
      }
      toast.error(extractApiErrorMessage(errorData) || 'Errore durante la creazione')
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data) => tipologieBoxApi.patch(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tipologie-box'] })
      queryClient.invalidateQueries({ queryKey: ['tipologie-box', id] })
      toast.success('Box aggiornata con successo')
      navigate('/box/tipologie')
    },
    onError: (error) => {
      const errorData = error.response?.data
      if (errorData) {
        setErrors({ ...errorData, prezzo_vendita_euro: errorData.prezzo_vendita_cents })
      }
      toast.error(extractApiErrorMessage(errorData) || 'Errore durante l\'aggiornamento')
    },
  })

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }))
    }
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setImmagine(file)
      setImmaginePreview(URL.createObjectURL(file))
    }
  }

  const removeImage = () => {
    setImmagine(null)
    setImmaginePreview(null)
  }

  // Gestione composizione (prodotti + box figlie)
  const getVoceKey = (voce) => `${voce.tipo}-${voce.prodotto || voce.box_figlia}`

  const mapComposizionePayload = (voce) => {
    const payload = {
      quantita: voce.quantita,
      note: voce.note || '',
    }
    if (voce.tipo === 'prodotto') {
      payload.prodotto = voce.prodotto
    } else {
      payload.box_figlia = voce.box_figlia
    }
    return payload
  }

  const addVoceComposizione = (voceSelezionata) => {
    if (!voceSelezionata) return

    const nuovaVoce = tipoVoceComposizione === 'prodotto'
      ? {
          tipo: 'prodotto',
          prodotto: voceSelezionata.id,
          box_figlia: null,
          nome: voceSelezionata.nome,
          codice: voceSelezionata.codice || '',
          costo_unitario_cents: voceSelezionata.prezzo_acquisto
            ? Math.round(parseFloat(voceSelezionata.prezzo_acquisto) * 100)
            : 0,
          quantita: 1,
          note: '',
        }
      : {
          tipo: 'box',
          prodotto: null,
          box_figlia: voceSelezionata.id,
          nome: voceSelezionata.nome,
          codice: voceSelezionata.codice || '',
          costo_unitario_cents: voceSelezionata.costo_totale_cents || 0,
          quantita: 1,
          note: '',
        }

    if (composizione.find((c) => getVoceKey(c) === getVoceKey(nuovaVoce))) {
      return
    }

    setComposizione([...composizione, nuovaVoce])
    setSearchComposizione('')
    setShowComposizioneDropdown(false)
  }

  const prodottiFiltrati = prodotti
    .filter((p) => {
      if (!searchComposizione.trim()) return true
      const search = searchComposizione.toLowerCase()
      return (
        p.nome?.toLowerCase().includes(search) ||
        p.codice?.toLowerCase().includes(search) ||
        p.categoria_nome?.toLowerCase().includes(search)
      )
    })
    .filter((p) => !composizione.find((c) => c.tipo === 'prodotto' && c.prodotto === p.id))

  const boxFiglieFiltrate = boxFiglie
    .filter((b) => !isEditing || b.id !== parseInt(id, 10))
    .filter((b) => {
      if (!searchComposizione.trim()) return true
      const search = searchComposizione.toLowerCase()
      return (
        b.nome?.toLowerCase().includes(search) ||
        b.codice?.toLowerCase().includes(search)
      )
    })
    .filter((b) => !composizione.find((c) => c.tipo === 'box' && c.box_figlia === b.id))

  const vociFiltrate = tipoVoceComposizione === 'prodotto'
    ? prodottiFiltrati
    : boxFiglieFiltrate
  const isSearchPending = searchComposizione.trim() !== debouncedSearchComposizione
    || (tipoVoceComposizione === 'prodotto' ? isFetchingProdotti : isFetchingBoxFiglie)

  const updateQuantita = (voceKey, quantita) => {
    setComposizione(composizione.map((c) =>
      getVoceKey(c) === voceKey ? { ...c, quantita: Math.max(1, parseInt(quantita) || 1) } : c
    ))
  }

  const updateNote = (voceKey, note) => {
    setComposizione(composizione.map((c) =>
      getVoceKey(c) === voceKey ? { ...c, note } : c
    ))
  }

  const removeVoce = (voceKey) => {
    setComposizione(composizione.filter((c) => getVoceKey(c) !== voceKey))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    // Validazione con utility centralizzata
    const newErrors = validateBox(formData)
    
    if (hasErrors(newErrors)) {
      setErrors(newErrors)
      return
    }

    const prezzoCents = euroToCents(formData.prezzo_vendita_euro)

    // Prepara dati
    let dataToSend
    
    if (immagine) {
      // Se c'è un'immagine, usa FormData
      const formDataToSend = new FormData()
      formDataToSend.append('nome', formData.nome.trim())
      formDataToSend.append('descrizione', formData.descrizione.trim())
      formDataToSend.append('configurazione', parseInt(formData.configurazione))
      formDataToSend.append('prezzo_vendita_cents', prezzoCents)
      formDataToSend.append('attivo', formData.attivo)
      formDataToSend.append('disponibile_ecommerce', formData.disponibile_ecommerce)
      formDataToSend.append('riutilizzabile_come_componente', formData.riutilizzabile_come_componente)
      formDataToSend.append(
        'composizione_input',
        JSON.stringify(composizione.map((c) => mapComposizionePayload(c)))
      )
      formDataToSend.append('immagine', immagine)
      dataToSend = formDataToSend
    } else {
      // Altrimenti usa JSON normale
      dataToSend = {
        nome: formData.nome.trim(),
        descrizione: formData.descrizione.trim(),
        configurazione: parseInt(formData.configurazione),
        prezzo_vendita_cents: prezzoCents,
        attivo: formData.attivo,
        disponibile_ecommerce: formData.disponibile_ecommerce,
        riutilizzabile_come_componente: formData.riutilizzabile_come_componente,
        composizione_input: composizione.map((c) => mapComposizionePayload(c)),
      }
    }

    if (isEditing) {
      updateMutation.mutate(dataToSend)
    } else {
      createMutation.mutate(dataToSend)
    }
  }

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(cents / 100)
  }

  const normalizeTreeItem = (item) => {
    const tipo = item.tipo || item.tipo_voce
    if (tipo === 'box') {
      return {
        tipo: 'box',
        id: item.box_figlia,
        nome: item.nome || item.box_figlia_nome || `Box #${item.box_figlia}`,
        codice: item.codice || item.box_figlia_codice || '',
        quantita: item.quantita || 1,
      }
    }
    return {
      tipo: 'prodotto',
      id: item.prodotto,
      nome: item.nome || item.prodotto_nome || `Prodotto #${item.prodotto}`,
      codice: item.codice || item.prodotto_codice || '',
      quantita: item.quantita || 1,
    }
  }

  const renderTree = (items, depth = 0, lineage = []) => {
    return items.map((rawItem, index) => {
      const item = normalizeTreeItem(rawItem)
      const rowKey = `${depth}-${item.tipo}-${item.id}-${index}`
      const isBox = item.tipo === 'box'
      const hasCycle = isBox && lineage.includes(item.id)
      const childRows = isBox ? (boxTreeMap[item.id]?.composizione || []) : []
      const canExpand = isBox && childRows.length > 0 && depth < 4 && !hasCycle

      return (
        <div key={rowKey} className="space-y-1">
          <div
            className="flex items-center gap-2 text-sm"
            style={{ marginLeft: `${depth * 16}px` }}
          >
            {isBox ? (
              <BoxIcon className="h-4 w-4 text-blue-600 shrink-0" />
            ) : (
              <Wine className="h-4 w-4 text-amber-600 shrink-0" />
            )}
            <span className="font-medium">{item.nome}</span>
            {item.codice ? (
              <span className="text-xs text-muted-foreground font-mono">{item.codice}</span>
            ) : null}
            <Badge variant="outline" className="text-xs">
              x{item.quantita}
            </Badge>
            {isBox ? (
              <Badge variant="secondary" className="text-xs">Box figlia</Badge>
            ) : null}
          </div>
          {hasCycle ? (
            <p className="text-xs text-red-600 ml-6">
              Ciclo rilevato in anteprima. Il backend bloccherà il salvataggio.
            </p>
          ) : null}
          {canExpand ? (
            <div className="space-y-1">
              {renderTree(childRows, depth + 1, [...lineage, item.id])}
            </div>
          ) : null}
          {isBox && childRows.length > 0 && depth >= 4 ? (
            <p className="text-xs text-muted-foreground ml-6">
              Altri livelli presenti, apri la box figlia per vedere il dettaglio completo.
            </p>
          ) : null}
        </div>
      )
    })
  }

  // Calcolo costi in tempo reale
  const configurazioneSelezionata = configurazioni.find(c => c.id === parseInt(formData.configurazione))
  const configurazioneDettaglio = configurazioneDettaglioData?.data
  const componentiConfigurazione = configurazioneDettaglio?.componenti_dettaglio || []
  const costoScatola = configurazioneSelezionata?.costo_totale_cents || 0
  const costoContenuto = composizione.reduce(
    (acc, c) => acc + (c.costo_unitario_cents || 0) * c.quantita,
    0
  )
  const costoTotale = costoScatola + costoContenuto
  const prezzoVendita = euroToCents(formData.prezzo_vendita_euro)
  const margine = prezzoVendita !== null && prezzoVendita > 0 ? prezzoVendita - costoTotale : null
  const marginePercentuale = prezzoVendita > 0 ? ((margine / prezzoVendita) * 100).toFixed(2) : 0

  const isPending = createMutation.isPending || updateMutation.isPending

  if (isEditing && isLoadingBox) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/box/tipologie">
            <ArrowLeftIcon className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {isEditing ? 'Modifica Box' : 'Nuova Box'}
          </h1>
          <p className="text-muted-foreground">
            {isEditing ? 'Modifica i dettagli della box' : 'Crea una nuova tipologia box'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6 md:grid-cols-3">
          {/* Colonna sinistra - Info base */}
          <div className="space-y-6">
            {/* Informazioni Base */}
            <Card>
              <CardHeader>
                <CardTitle>Informazioni Base</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="codice">Codice (automatico)</Label>
                  <Input
                    id="codice"
                    value={formData.codice}
                    disabled
                    placeholder={formData.codice ? '' : 'Verrà generato automaticamente'}
                  />
                  <p className="text-xs text-muted-foreground">
                    Generato automaticamente dal nome al salvataggio.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nome">Nome *</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => handleChange('nome', e.target.value)}
                    placeholder="Box Natale Grande"
                    className={errors.nome ? 'border-red-500' : ''}
                  />
                  {errors.nome && <p className="text-sm text-red-500">{errors.nome}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="descrizione">Descrizione</Label>
                  <Textarea
                    id="descrizione"
                    value={formData.descrizione}
                    onChange={(e) => handleChange('descrizione', e.target.value)}
                    placeholder="Descrizione della box..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Configurazione Scatola */}
            <Card>
              <CardHeader>
                <CardTitle>Configurazione Scatola</CardTitle>
                <CardDescription>Seleziona la scatola da utilizzare</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Configurazione *</Label>
                  <Select
                    value={formData.configurazione}
                    onValueChange={(value) => handleChange('configurazione', value)}
                  >
                    <SelectTrigger className={errors.configurazione ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Seleziona configurazione" />
                    </SelectTrigger>
                    <SelectContent>
                      {configurazioni.map((config) => (
                        <SelectItem key={config.id} value={config.id.toString()}>
                          {config.nome} - {formatCurrency(config.costo_totale_cents)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.configurazione && <p className="text-sm text-red-500">{errors.configurazione}</p>}
                </div>

                {configurazioneSelezionata && (
                  <div className="space-y-3 p-3 bg-muted rounded-lg">
                    <div>
                      <p className="text-sm font-medium">{configurazioneSelezionata.nome}</p>
                      <p className="text-sm text-muted-foreground">
                        Costo: {formatCurrency(configurazioneSelezionata.costo_totale_cents)}
                      </p>
                      {configurazioneDettaglio?.dimensioni_interne && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Dimensioni interne: {configurazioneDettaglio.dimensioni_interne}
                        </p>
                      )}
                    </div>

                    {componentiConfigurazione.length > 0 && (
                      <div className="space-y-1 border-t pt-2">
                        <p className="text-xs font-medium text-muted-foreground">Breakdown componenti</p>
                        {componentiConfigurazione.map((comp) => (
                          <div key={comp.id} className="flex items-center justify-between text-xs">
                            <span>{comp.componente_nome} x{comp.quantita}</span>
                            <span className="text-muted-foreground">
                              {formatCurrency(comp.costo_totale_cents || 0)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Immagine */}
            <Card>
              <CardHeader>
                <CardTitle>Immagine</CardTitle>
              </CardHeader>
              <CardContent>
                {immaginePreview ? (
                  <div className="relative">
                    <img
                      src={immaginePreview}
                      alt="Preview"
                      className="w-full aspect-square object-cover rounded-lg"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={removeImage}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full aspect-square border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50">
                    <PhotoIcon className="h-12 w-12 text-muted-foreground mb-2" />
                    <span className="text-sm text-muted-foreground">Clicca per caricare</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </label>
                )}
              </CardContent>
            </Card>

            {/* Stato */}
            <Card>
              <CardHeader>
                <CardTitle>Stato</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="attivo">Attiva</Label>
                  <Switch
                    id="attivo"
                    checked={formData.attivo}
                    onCheckedChange={(checked) => handleChange('attivo', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="ecommerce">Disponibile E-commerce</Label>
                  <Switch
                    id="ecommerce"
                    checked={formData.disponibile_ecommerce}
                    onCheckedChange={(checked) => handleChange('disponibile_ecommerce', checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="riutilizzabile">Riutilizzabile come box figlia</Label>
                    <p className="text-xs text-muted-foreground">
                      Se attiva, questa box può essere inserita dentro altre tipologie.
                    </p>
                  </div>
                  <Switch
                    id="riutilizzabile"
                    checked={formData.riutilizzabile_come_componente}
                    onCheckedChange={(checked) => handleChange('riutilizzabile_come_componente', checked)}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Colonna centrale e destra - Composizione e Prezzi */}
          <div className="md:col-span-2 space-y-6">
            {/* Prezzo e Margini */}
            <Card>
              <CardHeader>
                <CardTitle>Prezzo e Margini</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-5">
                  <div className="space-y-2">
                    <Label htmlFor="prezzo">Prezzo vendita (€) *</Label>
                    <Input
                      id="prezzo"
                      type="text"
                      inputMode="decimal"
                      aria-invalid={!!errors.prezzo_vendita_euro}
                      aria-describedby={errors.prezzo_vendita_euro ? 'prezzo-error' : undefined}
                      value={formData.prezzo_vendita_euro}
                      onChange={(e) => handleChange('prezzo_vendita_euro', e.target.value)}
                      placeholder="25,00"
                      className={errors.prezzo_vendita_euro ? 'border-red-500' : ''}
                    />
                    {errors.prezzo_vendita_euro && (
                      <p id="prezzo-error" className="text-sm text-red-500">{errors.prezzo_vendita_euro}</p>
                    )}
                    <p className="text-sm text-muted-foreground">Importo in euro, es. 25,50</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">Costo Scatola</p>
                    <p className="font-medium">{formatCurrency(costoScatola)}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">Costo Contenuto</p>
                    <p className="font-medium">{formatCurrency(costoContenuto)}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">Costo Totale</p>
                    <p className="font-medium">{formatCurrency(costoTotale)}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${margine === null ? 'bg-muted' : margine >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                    <p className="text-xs text-muted-foreground">Margine</p>
                    <p className={`font-medium ${margine === null ? 'text-muted-foreground' : margine >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {margine === null ? '—' : `${formatCurrency(margine)} (${marginePercentuale}%)`}
                    </p>
                  </div>
                </div>
                {formData.disponibile_ecommerce && (
                  <p className="text-xs text-muted-foreground mt-3">
                    SKU Shopify non gestito su Tipologie Box: il campo è stato rimosso lato backend.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Composizione Contenuto */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Composizione Contenuto</CardTitle>
                    <CardDescription>Aggiungi prodotti o box figlie riutilizzabili</CardDescription>
                  </div>
                  <Badge variant="secondary">
                    {composizione.reduce((acc, c) => acc + c.quantita, 0)} pezzi
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-[220px_1fr]">
                  <div className="space-y-2">
                    <Label>Tipo voce</Label>
                    <Select value={tipoVoceComposizione} onValueChange={setTipoVoceComposizione}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="prodotto">Prodotto</SelectItem>
                        <SelectItem value="box">Box figlia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Aggiungi voce - Autocomplete */}
                <div className="relative" ref={dropdownRef}>
                  <Input
                    placeholder={
                      tipoVoceComposizione === 'prodotto'
                        ? 'Cerca prodotto per nome, codice o categoria...'
                        : 'Cerca box figlia per nome o codice...'
                    }
                    value={searchComposizione}
                    onChange={(e) => {
                      setSearchComposizione(e.target.value)
                      setShowComposizioneDropdown(true)
                    }}
                    onFocus={() => setShowComposizioneDropdown(true)}
                    className="pr-10"
                  />
                  {searchComposizione && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchComposizione('')
                        setShowComposizioneDropdown(false)
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      ✕
                    </button>
                  )}
                  
                  {/* Dropdown voci filtrate */}
                  {showComposizioneDropdown && !isSearchPending && vociFiltrate.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-background border rounded-lg shadow-lg max-h-[300px] overflow-auto">
                      {vociFiltrate.slice(0, 10).map((voce) => (
                        <button
                          key={voce.id}
                          type="button"
                          onClick={() => addVoceComposizione(voce)}
                          className="w-full px-4 py-3 text-left hover:bg-accent transition-colors border-b last:border-b-0"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="font-medium">{voce.nome}</p>
                              <p className="text-sm text-muted-foreground">
                                {voce.codice} {voce.categoria_nome && `• ${voce.categoria_nome}`}
                              </p>
                            </div>
                            <p className="text-sm font-medium ml-4">
                              {tipoVoceComposizione === 'prodotto'
                                ? (voce.prezzo_acquisto ? `€${parseFloat(voce.prezzo_acquisto).toFixed(2)}` : '€0,00')
                                : `€${((voce.costo_totale_cents || 0) / 100).toFixed(2)}`}
                            </p>
                          </div>
                        </button>
                      ))}
                      {vociFiltrate.length > 10 && (
                        <div className="px-4 py-2 text-sm text-muted-foreground text-center border-t">
                          +{vociFiltrate.length - 10} altri risultati...
                        </div>
                      )}
                    </div>
                  )}
                  
                  {showComposizioneDropdown && searchComposizione && isSearchPending && (
                    <div className="absolute z-50 w-full mt-1 bg-background border rounded-lg shadow-lg p-4 text-center text-muted-foreground">
                      Ricerca in corso...
                    </div>
                  )}

                  {showComposizioneDropdown && searchComposizione && !isSearchPending && vociFiltrate.length === 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-background border rounded-lg shadow-lg p-4 text-center text-muted-foreground">
                      Nessun elemento trovato
                    </div>
                  )}
                </div>

                <Separator />

                {/* Lista composizione */}
                {composizione.length > 0 ? (
                  <div className="readable-rows overflow-hidden rounded-lg border">
                    {composizione.map((comp) => {
                      const voceKey = getVoceKey(comp)
                      return (
                        <div
                          key={voceKey}
                          className="readable-row space-y-3 p-3"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-muted rounded flex items-center justify-center">
                              {comp.tipo === 'prodotto'
                                ? <Wine className="h-5 w-5 text-muted-foreground" />
                                : <BoxIcon className="h-5 w-5 text-muted-foreground" />}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium">{comp.nome}</p>
                              <p className="text-sm text-muted-foreground">
                                {comp.codice ? `${comp.codice} • ` : ''}
                                {comp.tipo === 'prodotto' ? 'Prodotto' : 'Box figlia'} • {formatCurrency(comp.costo_unitario_cents || 0)} cad.
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                min="1"
                                value={comp.quantita}
                                onChange={(e) => updateQuantita(voceKey, e.target.value)}
                                className="w-20 text-center"
                              />
                              <span className="text-sm text-muted-foreground">
                                = {formatCurrency((comp.costo_unitario_cents || 0) * comp.quantita)}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="text-red-600"
                                onClick={() => removeVoce(voceKey)}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor={`nota-composizione-${voceKey}`}>Nota composizione</Label>
                            <Input
                              id={`nota-composizione-${voceKey}`}
                              value={comp.note || ''}
                              onChange={(e) => updateNote(voceKey, e.target.value)}
                              placeholder="Nota opzionale (es. versione natalizia)"
                              maxLength={200}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Nessuna voce aggiunta</p>
                    <p className="text-sm">Usa il menu sopra per aggiungere prodotti o box figlie</p>
                  </div>
                )}

                {getFieldErrorMessages('composizione_input').length > 0 && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-3 space-y-1">
                    <p className="text-sm font-medium text-red-700">Errore composizione</p>
                    {getFieldErrorMessages('composizione_input').map((message, idx) => (
                      <p key={`${message}-${idx}`} className="text-sm text-red-700">
                        • {message}
                      </p>
                    ))}
                  </div>
                )}

                {composizione.length > 0 && (
                  <div className="rounded-lg border p-3 space-y-2">
                    <p className="text-sm font-medium">Anteprima struttura annidata</p>
                    <p className="text-xs text-muted-foreground">
                      La preview mostra la gerarchia box figlie/prodotti per verificare subito la composizione.
                    </p>
                    <div className="space-y-2">
                      {renderTree(composizione)}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Azioni */}
            <div className="flex justify-end gap-4">
              <Button type="button" variant="outline" asChild>
                <Link to="/box/tipologie">Annulla</Link>
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Salvataggio...' : (isEditing ? 'Salva Modifiche' : 'Crea Box')}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  )
}
